/* global CriteriaHelper */
import React from 'react';
import { translate } from '../../../common/utility/DisplayPrep';
import { convertUTCToLocalDate } from '../../../components/DatePicker';

const { READ_ONLY_FEATURES } = process.env;

const actions = READ_ONLY_FEATURES === 'true' ? ['edit'] : ['drag', 'edit', 'delete'];

const defaultUiConfig = {
    name: 'Predicate Part',
    withActions: [...actions],
    primaryKey: 'uuid',
    pageSize: 10,
    onFormMount: (data, hookFxns) => {
        if (data?.dataInputClassType) {
            hookFxns.updateClassTypeFn(data.dataInputClassType);
        }
        if (data?.conceptSelectionType) {
            hookFxns.updateConceptSelectionType(data.conceptSelectionType);
        }
        if (data?.partType) {
            hookFxns.updatePartType(data.partType);
        }
        if (data?.dataInputNodeDTO) {
            hookFxns.updateDataInputNodeDTO(data?.dataInputNodeDTO?.nodeId);
        }
        if (data?.resourceType) {
            hookFxns.updateResourceType(data?.resourceType, true);
            hookFxns.maybeUpdateResourceOptionsMap(data?.resourceType);
        }
        if (data?.criteriaResourceDTO) {
            hookFxns.updateResource(data?.criteriaResourceDTO?.resourceId, true);
            hookFxns.maybeUpdateResourceParamOptionsMap(data?.criteriaResourceDTO?.resourceId);
        }
        if (data?.criteriaResourceParamDTO) {
            hookFxns.updateResourceParam(data?.criteriaResourceParamDTO?.paramId);
        }

        return null;
    },
    properties: {
        partId: {
            name: 'ID',
            defaultContent: '',
            sortable: false,
            input: (v) => {
                return typeof v.predicateId !== 'undefined' ? 'valueDisplay' : null;
            }
        },
        partAlias: {
            name: 'Alias (optional)',
            defaultContent: '',
            rowDisplay: true,
            sortable: false,
            input: 'text',
            additionalProperties: {
                Cell: (data) => {
                    const part = data?.cell?.row?.original ?? null;
                    const label =
                        part?.partAlias && part.partAlias !== '' && part?.partAlias !== null
                            ? part?.partAlias
                            : CriteriaHelper.getPartLabel(part) ?? '';

                    // return <span style={part?.dtoState === 'DELETED' ? { color: 'red' } : {}}>{label}</span>;
                    return (
                        <span>
                            {label}
                            {part?.functionEnd && <span>)</span>}
                            {part?.parameterEnd && <span>,</span>}
                        </span>
                    );
                }
            }
        },
        predicatePartOrder: {
            name: 'Order',
            defaultContent: '',
            rowDisplay: false,
            sortable: false,
            input: null
        },
        overridable: {
            defaultContent: false,
            rowDisplay: false,
            name: 'Overridable?',
            input: 'checkbox',
            inputProps: {
                checkboxlabel: 'Overridable?'
            }
        },
        parameterEnd: {
            defaultContent: false,
            rowDisplay: false,
            name: 'Parameter End?',
            input: 'checkbox',
            inputProps: {
                checkboxlabel: 'Parameter End?'
            }
        },
        functionEnd: {
            defaultContent: false,
            rowDisplay: false,
            name: 'Function End?',
            input: 'checkbox',
            inputProps: {
                checkboxlabel: 'Function End?'
            }
        },
        partType: {
            name: 'Part Type',
            defaultContent: '',
            min: 0,
            max: 2048,
            rowDisplay: false,
            sortable: false,
            nullable: false,
            input: 'select',
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updatePartType(v);
            }
        },
        uuid: {
            rowDisplay: false,
            sortable: true,
            name: 'Uuid'
        },
        lastModId: {
            rowDisplay: false,
            sortable: true,
            name: 'Last Modified ID'
        },
        dataInputClassType: {
            name: 'Class Type',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' ? 'select' : null;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateClassTypeFn(v);
            }
        },
        dataInputBoolean: {
            name: 'Value',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'Boolean' ? 'checkbox' : null;
            },
            inputProps: {
                checkboxlabel: 'Is True?'
            }
        },
        conceptSelectionType: {
            name: 'Concept Selection Type',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'Concept' ? 'select' : null;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateConceptSelectionType(v, true);
            }
        },
        singleConceptSelectionMode: {
            name: 'Limit To Single Selection?',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'Concept' ? 'checkbox' : null;
            },
            inputProps: {
                checkboxlabel: 'Limit To Single Selection?'
            }
        },
        dataInputDate1: {
            name: 'dataInputDate1',
            rowDisplay: false,
            sortable: false,
            label: (v) => {
                return v?.dataInputClassType === 'DateRange' ? 'From' : 'Value';
            },
            input: (v) => {
                return v?.partType === 'DataInput' && ['Date', 'DateRange'].includes(v?.dataInputClassType)
                    ? 'date'
                    : null;
            },
            accessorOverride: (v) => {
                if (typeof v !== 'undefined') {
                    if ((typeof v === 'string' && v !== '') || v instanceof Date) {
                        return convertUTCToLocalDate(v);
                    } else if (typeof v === 'object' && Object.hasOwn(v, 'dataInputDate1')) {
                        return convertUTCToLocalDate(v.dataInputDate1);
                    }
                }
                return undefined;
            }
        },
        dataInputDate2: {
            name: 'dataInputDate2',
            label: 'To',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'DateRange' ? 'date' : null;
            },
            accessorOverride: (v) => {
                if (typeof v !== 'undefined') {
                    if ((typeof v === 'string' && v !== '') || v instanceof Date) {
                        return convertUTCToLocalDate(v);
                    } else if (typeof v === 'object' && Object.hasOwn(v, 'dataInputDate2')) {
                        return convertUTCToLocalDate(v.dataInputDate2);
                    }
                }
                return undefined;
            }
        },
        defaultIdentifierRoot: {
            name: 'Root',
            defaultContent: '',
            min: 0,
            max: 1024,
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'Identifier' ? 'text' : null;
            }
        },
        defaultIdentifierExtension: {
            name: 'Extension',
            defaultContent: '',
            min: 0,
            max: 1024,
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && v?.dataInputClassType === 'Identifier' ? 'text' : null;
            }
        },
        dataInputNumeric: {
            name: 'Value',
            min: 0,
            max: 1024,
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'DataInput' && ['Numeric', 'Quantity'].includes(v?.dataInputClassType)
                    ? 'text'
                    : null;
            },
            inputProps: {
                type: 'number',
                step: '1'
            },
            label: (v) => {
                return v?.dataInputClassType === 'Quantity' ? 'Qty' : 'Value';
            }
        },
        nodeLabel: {
            name: 'Model Element Label',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'ModelElement' ? 'text' : null;
            }
        },
        dataInputNodeDTO: {
            name: 'Model Element',
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'ModelElement' ? 'select' : null;
            },
            accessorOverride: (v) => {
                return v && v?.nodeId;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateDataInputNodeDTO(v);
            }
        },
        text: {
            name: 'Text',
            min: 0,
            max: 512,
            rowDisplay: false,
            sortable: false,
            input: (v) => {
                return v?.partType === 'Text' ||
                    (v?.partType === 'DataInput' &&
                        ['Quantity', 'String', 'Keyword', 'Eval'].includes(v?.dataInputClassType))
                    ? 'text'
                    : null;
            },
            label: (v) => {
                return v?.dataInputClassType === 'Quantity' ? 'Unit' : 'Value';
            }
        },
        lastModDatetime: {
            rowDisplay: false,
            displayPrep: 'FullDate',
            name: 'Last Updated',
            additionalProperties: {
                Cell: ({ cell: { value } }) => {
                    return translate.ReadableDateTimeColumn(value);
                }
            }
        },
        createId: {
            rowDisplay: false,
            name: 'Create ID'
        },
        createDatetime: {
            rowDisplay: false,
            name: 'Create Datetime'
        },
        resourceType: {
            name: 'Resource Type',
            sortable: true,
            rowDisplay: false,
            nullable: true,
            input: (v) => {
                return v?.partType === 'Resource' ? 'select' : null;
            },
            preChangeBind: (v, onChange, hookFxns) => {
                onChange('criteriaResourceDTO', {}, false);
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateResourceType(v);
                hookFxns.maybeUpdateResourceOptionsMap(v);
            }
        },
        criteriaResourceDTO: {
            name: 'Resource',
            sortable: true,
            rowDisplay: false,
            nullable: true,
            selectClearable: true,
            input: (v) => {
                return v?.partType === 'Resource' && typeof v.resourceType !== 'undefined' ? 'select' : null;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateResource(v);
                hookFxns.maybeUpdateResourceParamOptionsMap(v);
            },
            accessorOverride: (v) => {
                return typeof v === 'string' ? v : v && v?.resourceId;
            }
        },
        criteriaResourceParamDTO: {
            name: 'Resource Selection',
            sortable: true,
            rowDisplay: false,
            nullable: true,
            input: (v) => {
                return v?.partType === 'Resource' && v.resourceType === 'Operator' ? 'select' : null;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateResourceParam(v);
            },
            accessorOverride: (v) => {
                return v && v?.paramId;
            }
        }
    },
    newDoc: {
        '@class': 'org.cdsframework.dto.CriteriaPredicatePartDTO',
        createDatetime: null,
        createId: null,
        dataInputBoolean: false,
        dataInputNodeDTO: null,
        dtoState: 'NEW',
        functionEnd: false,
        lastModDatetime: null,
        lastModId: null,
        nodeLabel: null,
        overridable: false,
        parameterEnd: false,
        partAlias: null,
        partId: null,
        partType: null,
        predicateId: null,
        predicatePartConcepts: [],
        predicatePartOrder: null,
        predicatePartRels: [],
        uuid: null
    }
    // sortBy: {
    //     columnId: 'predicatePartOrder',
    //     desc: false
    // }
};

export default defaultUiConfig;
